import React from 'react';
import { X, Layers, Clock, ShieldCheck, AlertTriangle, CheckCircle2, Info } from 'lucide-react';
import { DetailedFodmapStackingReport, FodmapSubtypeLoad } from '../lib/types';

interface FodmapStackingDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  report: DetailedFodmapStackingReport;
}

const SUBTYPE_INFO: Record<string, { family: string; examples: string; limitTip: string }> = {
  Fructans: {
    family: 'Oligosaccharides',
    examples: 'Wheat, garlic, onion, leeks, barley, inulin',
    limitTip: 'Space wheat and allium portions at least 4 hours apart.',
  },
  GOS: {
    family: 'Galacto-oligosaccharides',
    examples: 'Legumes, chickpeas, lentils, cashews, pistachios',
    limitTip: 'Canned and thoroughly rinsed legumes contain significantly lower GOS.',
  },
  Lactose: {
    family: 'Disaccharides',
    examples: 'Cow milk, regular yogurt, ice cream, soft fresh cheeses',
    limitTip: 'Use certified lactose-free dairy or hard cheeses (cheddar, parmesan).',
  },
  'Excess Fructose': {
    family: 'Monosaccharides',
    examples: 'Honey, mango, apples, pears, high fructose corn syrup, asparagus',
    limitTip: 'Pair high-fructose fruits with glucose or choose berries and citrus.',
  },
  Sorbitol: {
    family: 'Polyols',
    examples: 'Avocado, blackberries, plums, peaches, green bell peppers',
    limitTip: 'Keep avocado servings to 30g and avoid stone fruits within 4 hours.',
  },
  Mannitol: {
    family: 'Polyols',
    examples: 'Button mushrooms, cauliflower, celery, sweet potatoes',
    limitTip: 'Substitute oyster mushrooms or rinsed canned champignons.',
  },
};

export const FodmapStackingDetailModal: React.FC<FodmapStackingDetailModalProps> = ({
  isOpen,
  onClose,
  report,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 dark:bg-black/70 backdrop-blur-sm p-0 sm:p-4 animate-in fade-in duration-200">
      <div className="w-full max-w-2xl bg-white dark:bg-zinc-950 border border-slate-200 dark:border-zinc-800 sm:rounded-2xl rounded-t-2xl max-h-[92vh] flex flex-col shadow-2xl overflow-hidden text-slate-900 dark:text-zinc-100">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-zinc-800 bg-white/95 dark:bg-zinc-900/60 sticky top-0 z-10">
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-xl bg-slate-100 dark:bg-zinc-800 text-slate-800 dark:text-zinc-200">
              <Layers className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-sm font-semibold tracking-wider uppercase text-slate-900 dark:text-zinc-100">
                FODMAP Stacking Analysis
              </h2>
              <p className="text-xs text-slate-500 dark:text-zinc-400">Clinical 4-6h Gut Transit Standard</p>
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="w-10 h-10 flex items-center justify-center rounded-xl bg-slate-100 hover:bg-slate-200 dark:bg-zinc-800 dark:hover:bg-zinc-700 text-slate-600 hover:text-slate-900 dark:text-zinc-300 dark:hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable Body */}
        <div className="overflow-y-auto p-5 space-y-6 text-slate-800 dark:text-zinc-200">
          {/* Top Status Overview */}
          <div
            className={`p-4 rounded-xl border ${
              report.isStackingWarning
                ? 'bg-rose-50 dark:bg-rose-950/20 border-rose-200 dark:border-rose-800/40 text-rose-900 dark:text-rose-200'
                : report.highestPercent >= 75
                ? 'bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800/40 text-amber-900 dark:text-amber-200'
                : 'bg-emerald-50 dark:bg-emerald-950/20 border-emerald-200 dark:border-emerald-800/40 text-emerald-900 dark:text-emerald-200'
            }`}
          >
            <div className="flex items-start gap-3">
              {report.isStackingWarning ? (
                <AlertTriangle className="w-5 h-5 text-rose-600 dark:text-rose-400 shrink-0 mt-0.5" />
              ) : report.highestPercent >= 75 ? (
                <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
              ) : (
                <ShieldCheck className="w-5 h-5 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
              )}
              <div className="space-y-1">
                <div className="flex items-center gap-2 font-medium text-sm">
                  <span>
                    {report.isStackingWarning
                      ? 'Stacking Overload Detected'
                      : report.highestPercent >= 75
                      ? 'Approaching Stacking Limit'
                      : 'Plate is Within Safe Limits'}
                  </span>
                  <span className="text-xs px-2 py-0.5 rounded bg-white/80 dark:bg-zinc-900/80 border border-slate-200 dark:border-zinc-800 text-slate-700 dark:text-zinc-300 font-mono">
                    Max: {report.highestPercent}%
                  </span>
                </div>
                <p className="text-xs opacity-90 leading-relaxed">
                  {report.isStackingWarning
                    ? `Cumulative ${report.highestCategory} intake exceeds single-window digestive capacity. Consuming multiple sources of the same carbohydrate family within 4-5 hours can trigger symptoms.`
                    : report.highestPercent >= 75
                    ? `Highest category is ${report.highestCategory} at ${report.highestPercent}% of tolerance. Allow 4 hours before consuming more of this group.`
                    : 'All 6 FODMAP carbohydrate categories are safely buffered below physiological saturation thresholds.'}
                </p>
              </div>
            </div>
          </div>

          {/* Rolling Window Context */}
          {report.priorMealLookback && report.priorMealLookback.mealsCount > 0 && (
            <div className="flex items-center gap-3 p-3.5 rounded-xl bg-slate-50 dark:bg-zinc-900/70 border border-slate-200 dark:border-zinc-800 text-xs text-slate-600 dark:text-zinc-300">
              <Clock className="w-4 h-4 text-slate-400 dark:text-zinc-400 shrink-0" />
              <div>
                <span className="font-semibold text-slate-900 dark:text-zinc-200">
                  {report.priorMealLookback.mealsCount} Prior Meal(s) Analyzed
                </span>{' '}
                within the last {report.priorMealLookback.windowHours} hours. Residual fermentable
                carbohydrates in the small intestine are included in these scores.
              </div>
            </div>
          )}

          {/* 6 FODMAP Categories Progress Bars */}
          <div className="space-y-4">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-zinc-400">
              Category Breakdown (6 Carbohydrate Families)
            </h3>
            <div className="grid grid-cols-1 gap-3">
              {report.subtypes.map((sub: FodmapSubtypeLoad) => {
                const info = SUBTYPE_INFO[sub.subtype] || {
                  family: '',
                  examples: '',
                  limitTip: '',
                };
                const isOver = sub.status === 'Overload';
                const isCaution = sub.status === 'Caution';

                return (
                  <div
                    key={sub.subtype}
                    className="p-4 rounded-xl bg-slate-50 dark:bg-zinc-900 border border-slate-200 dark:border-zinc-800 space-y-3"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-slate-900 dark:text-zinc-100">{sub.subtype}</span>
                          <span className="text-[10px] uppercase font-mono px-1.5 py-0.5 rounded bg-slate-200 dark:bg-zinc-800 text-slate-600 dark:text-zinc-400">
                            {info.family}
                          </span>
                        </div>
                        <p className="text-[11px] text-slate-500 dark:text-zinc-400 mt-0.5">{info.examples}</p>
                      </div>

                      <div className="text-right">
                        <div className="flex items-center gap-1.5 justify-end">
                          <span
                            className={`w-2 h-2 rounded-full ${
                              isOver
                                ? 'bg-rose-500'
                                : isCaution
                                ? 'bg-amber-500'
                                : 'bg-emerald-500'
                            }`}
                          />
                          <span
                            className={`text-xs font-semibold font-mono ${
                              isOver
                                ? 'text-rose-600 dark:text-rose-400'
                                : isCaution
                                ? 'text-amber-600 dark:text-amber-400'
                                : 'text-emerald-600 dark:text-emerald-400'
                            }`}
                          >
                            {sub.percentOfThreshold}%
                          </span>
                        </div>
                        <span className="text-[10px] text-slate-500 dark:text-zinc-400 uppercase tracking-wider">
                          {sub.status}
                        </span>
                      </div>
                    </div>

                    {/* Progress Track */}
                    <div className="w-full h-2 rounded-full bg-slate-200 dark:bg-zinc-800 overflow-hidden">
                      <div
                        className={`h-full transition-all duration-300 rounded-full ${
                          isOver ? 'bg-rose-500' : isCaution ? 'bg-amber-500' : 'bg-emerald-500'
                        }`}
                        style={{ width: `${Math.min(100, sub.percentOfThreshold)}%` }}
                      />
                    </div>

                    {/* Contributing Ingredients */}
                    {sub.contributingIngredients.length > 0 && (
                      <div className="pt-2 border-t border-slate-200 dark:border-zinc-800/80 space-y-1.5">
                        <div className="text-[11px] font-medium text-slate-500 dark:text-zinc-400">
                          Contributing Ingredients:
                        </div>
                        <div className="space-y-1">
                          {sub.contributingIngredients.map((item, idx) => (
                            <div
                              key={idx}
                              className="flex items-center justify-between text-xs text-slate-700 dark:text-zinc-300 bg-white dark:bg-zinc-950/60 px-2.5 py-1.5 rounded-lg border border-slate-200 dark:border-zinc-800/60"
                            >
                              <div className="flex items-center min-w-0 pr-2">
                                <span className="truncate font-medium text-xs">{item.name}</span>
                              </div>
                              <div className="flex items-center gap-2 shrink-0">
                                <span className="text-slate-500 dark:text-zinc-400 font-mono text-[11px]">
                                  {item.grams}g
                                </span>
                                <span
                                  className={`text-[11px] font-mono font-medium ${
                                    item.subScore >= 1.0
                                      ? 'text-rose-600 dark:text-rose-400'
                                      : item.subScore >= 0.5
                                      ? 'text-amber-600 dark:text-amber-400'
                                      : 'text-slate-600 dark:text-zinc-400'
                                  }`}
                                >
                                  +{Math.round(item.subScore * 100)}%
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Clinical Educational Walkthrough */}
          <div className="p-4 rounded-xl bg-slate-50 dark:bg-zinc-900/60 border border-slate-200 dark:border-zinc-800 space-y-2 text-xs text-slate-600 dark:text-zinc-400">
            <div className="flex items-center gap-2 text-slate-900 dark:text-zinc-200 font-semibold">
              <Info className="w-4 h-4 text-slate-500 dark:text-zinc-400" />
              <span>How Stacking Works (Clinical Research)</span>
            </div>
            <p className="leading-relaxed">
              FODMAP stacking occurs when multiple foods containing the same carbohydrate subtype
              are eaten in the same meal or within 4–6 hours. While each food may be tested safe in
              isolation, their combined osmotic load and gas fermentation in the colon can cross your
              personal tolerance threshold.
            </p>
          </div>
        </div>

        {/* Footer Button */}
        <div className="p-4 border-t border-slate-200 dark:border-zinc-800 bg-white/95 dark:bg-zinc-900/80 sticky bottom-0">
          <button
            onClick={onClose}
            className="w-full py-3.5 px-4 rounded-xl bg-slate-900 hover:bg-slate-800 text-white dark:bg-zinc-100 dark:hover:bg-white dark:text-zinc-950 font-semibold text-sm tracking-wide active:scale-[0.99] transition-all shadow-md"
          >
            Close Analysis
          </button>
        </div>
      </div>
    </div>
  );
};
